/**
 * World-map barbarian village levels align 1:1 with PvE campaign rounds (see section-page / map regions).
 */
export const BARBARIAN_PVE_MIN_LEVEL = 1
export const BARBARIAN_PVE_MAX_LEVEL = 21

export function clampBarbarianPveLevel(level: number): number {
  return Math.max(
    BARBARIAN_PVE_MIN_LEVEL,
    Math.min(BARBARIAN_PVE_MAX_LEVEL, Math.floor(level) || 1),
  )
}

/** Resource-icon URLs (mirror of BarbarianVillageInfoModal LOOT_ICONS). */
export const BARBARIAN_RESOURCE_ICONS: Record<string, string> = {
  wheat: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/land/wheat-icon.webp',
  bread: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/land/bread-icon.webp',
  wood: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/land/wood-icon.webp',
  stone: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/land/stone-icon.webp',
  iron: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/land/iron-icon.webp',
  usdt: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/mining-factory/usdt-icon.webp',
}

/** Per-resource barbarian-village artwork base (filename suffix differs; usdt → "usd"). */
const BARBARIAN_VILLAGE_IMAGE_BASE =
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/barbarians/barbarian-'

export interface BarbarianRewardBand {
  /** Reward resource key (matches User balance + WorldBarbarianVillage.rewardType on the backend). */
  resourceKey: 'wheat' | 'bread' | 'wood' | 'stone' | 'iron' | 'usdt'
  /** Display label for the resource. */
  label: string
  /** Reward amount range across the resource's villages (display string). */
  amounts: string
  /** Resource icon URL. */
  icon: string
  /** Full-size barbarian-village artwork for this resource (guide modal). */
  image: string
  /** Village names that reward this resource (the map shows the NAME, not a level). */
  names: string[]
}

/**
 * What each barbarian village rewards, grouped by resource. A village shows only its NAME on the world map;
 * its name comes from the matching group here, so players can look up a name and see what it gives. Paid on a
 * successful attack AND per day of ownership. MIRRORS the backend
 * eclipsera_backend/packages/core/src/constants/barbarianVillageNamesByResource.js + barbarianVillageRewards.js
 * — keep names + amounts in sync if either side changes.
 */
export const BARBARIAN_REWARD_BANDS: BarbarianRewardBand[] = [
  {
    resourceKey: 'wheat',
    label: 'Wheat',
    amounts: '55 – 153',
    icon: BARBARIAN_RESOURCE_ICONS.wheat,
    image: `${BARBARIAN_VILLAGE_IMAGE_BASE}wheat.webp`,
    names: [
      'Goldengrain Camp', 'Harvestmaw Hold', 'Chafflands Den', 'Grainreach Warren', 'Sicklewind Roost',
      'Amberfield Bluff', 'Husktooth Camp', 'Cornsilk Hollow', 'Bushelgore Hold', 'Granary Den',
      'Sheafmaw Camp', 'Tasselwind Roost', 'Furrowfang Warren', 'Goldchaff Hold', "Reapers' Hollow",
      'Threshing Den', 'Wheatmaw Camp', 'Stalkfield Bluff',
    ],
  },
  {
    resourceKey: 'bread',
    label: 'Bread',
    amounts: '43 – 126',
    icon: BARBARIAN_RESOURCE_ICONS.bread,
    image: `${BARBARIAN_VILLAGE_IMAGE_BASE}bread.webp`,
    names: [
      'Ovenfang Hold', 'Hearthmaw Camp', 'Crustbreak Den', 'Loafgore Roost', 'Yeastmire Warren',
      'Emberloaf Bluff', 'Bakestone Hold', 'Crumbfang Den', 'Sourdough Hollow', 'Kilnmaw Camp',
      'Doughgrim Roost', 'Ryecrust Warren', 'Mealmaw Den', 'Toastfang Camp', 'Ovenrest Hold',
      'Flourgore Den', 'Browned Hollow', 'Hearthcrust Bluff',
    ],
  },
  {
    resourceKey: 'wood',
    label: 'Wood',
    amounts: '31 – 101',
    icon: BARBARIAN_RESOURCE_ICONS.wood,
    image: `${BARBARIAN_VILLAGE_IMAGE_BASE}wood.webp`,
    names: [
      'Timberfang Hold', 'Sapmaw Camp', 'Barkgrim Den', 'Pinereach Roost', 'Splitlog Warren',
      'Resinmire Bluff', 'Oakenmaw Hold', 'Knotfang Den', 'Sawtooth Hollow', 'Hollowbark Camp',
      'Greenwood Roost', 'Limbgore Warren', 'Mossfang Den', 'Cedarmaw Camp', 'Twigsnap Hold',
      'Rootfang Den', 'Pinetar Hollow', 'Birchgore Bluff',
    ],
  },
  {
    resourceKey: 'stone',
    label: 'Stone',
    amounts: '23 – 82',
    icon: BARBARIAN_RESOURCE_ICONS.stone,
    image: `${BARBARIAN_VILLAGE_IMAGE_BASE}stone.webp`,
    names: [
      'Quarryfang Hold', 'Granitemaw Camp', 'Slatebreak Den', 'Boulderreach Roost', 'Chiselgore Warren',
      'Flintmire Bluff', 'Cragmaw Hold', 'Rubblefang Den', 'Marblerock Hollow', 'Gravelpit Camp',
      'Cobblemaw Roost', 'Shalefang Warren', 'Quartzcrag Den', 'Bedrock Camp', 'Slabfang Hold',
      'Cairnstone Den', 'Limecrag Hollow', 'Basaltgore Bluff',
    ],
  },
  {
    resourceKey: 'iron',
    label: 'Iron',
    amounts: '15 – 67',
    icon: BARBARIAN_RESOURCE_ICONS.iron,
    image: `${BARBARIAN_VILLAGE_IMAGE_BASE}iron.webp`,
    names: [
      'Ironfang Hold', 'Slagmaw Camp', 'Forgebreak Den', 'Oreveins Roost', 'Anvilgore Warren',
      'Cindermire Bluff', 'Rustmaw Hold', 'Smeltfang Den', 'Hammerrock Hollow', 'Ingotmaw Camp',
      'Steelfang Roost', 'Drossgore Warren', 'Emberforge Den', 'Bloomery Camp', 'Pigiron Hold',
      'Scoriafang Den', 'Cinderforge Hollow', 'Blackiron Bluff',
    ],
  },
  {
    resourceKey: 'usdt',
    label: 'USDT',
    amounts: '0.05',
    icon: BARBARIAN_RESOURCE_ICONS.usdt,
    image: `${BARBARIAN_VILLAGE_IMAGE_BASE}usd.webp`,
    names: [
      'Coinvault Hold', 'Bullion Den', 'Treasuremaw Camp', 'Goldhoard Roost', 'Mintfang Warren',
      'Vaultgore Bluff', 'Ducat Hollow', 'Sovereign Hold', 'Greedmaw Den', "Riches' Roost",
      'Coffergrim Camp', 'Fortune Warren',
    ],
  },
]

/** The 6 per-resource barbarian-village artwork URLs (for the guide modal + Vercel blob preload). */
export const BARBARIAN_VILLAGE_GUIDE_IMAGE_URLS: readonly string[] = BARBARIAN_REWARD_BANDS.map(
  (b) => b.image,
)
